/**
 * Terminal-outcome counting (ENG-4321).
 *
 * A retry-prone step that fails a few times and then succeeds must count as
 * ONE terminal success, not a string of failures — "a bridge that fails 30x
 * then connects is NOT a failure". This module owns that decision as a PURE
 * function over (attempt number, elapsed window time, this attempt's outcome),
 * plus a stateful per-key controller that guarantees the terminal event fires
 * AT MOST ONCE per bounded window even under React re-renders.
 *
 * Two dimensions bound the window; whichever is hit first makes a FAILED
 * attempt terminal:
 *   - time:   the wall-clock window since the first attempt, and
 *   - count:  the number of attempts.
 * A SUCCESS is always terminal — the step recovered, so we stop and count one
 * success. A FAILURE is terminal only when no further in-window attempt is
 * possible (the count cap is hit or the time window is exceeded); an earlier
 * failed attempt that a later success supersedes is NOT counted (that is the
 * whole point of the ticket).
 *
 * Per rule: reference-rules-in-comments (ai.knowledge/global) — this module
 * implements ENG-4321's counting rule; the ENG-4337 boundary is called out at
 * {@link decideTerminalOutcome} so retry-storm volume is never conflated with
 * the terminal failure count.
 */

/**
 * Default terminal-outcome window (ms) — the NO-BUDGET FALLBACK only. WHY 60s:
 * it is ENG-4321's plan default for how long we wait for a retry-prone step to
 * recover before treating the current attempt as a terminal failure. TUNABLE and
 * AWAITING Product confirmation. A host loop with its own retry budget passes
 * that budget into {@link createTerminalOutcomeController}'s `beginWindow`, so the
 * window is NEVER tighter than the loop it observes (ENG-4321 B1: the LiveKit
 * video recovery loop budgets 120s — a genuine late recovery must not be booked
 * as a terminal failure). This constant binds only when a host supplies no
 * budget. Exported so a call site tunes the fallback without editing the logic.
 */
export const TERMINAL_WINDOW_MS = 60_000;

/**
 * Default terminal-outcome attempt cap — the NO-BUDGET FALLBACK only. WHY 10:
 * ENG-4321's plan default for the maximum number of attempts inside one window
 * before a still-failing step is treated as terminal. TUNABLE and AWAITING
 * Product confirmation. A host loop passes its own cap into `beginWindow`
 * (ENG-4321 B1: the LiveKit video loop budgets 30 attempts); this constant binds
 * only when a host supplies none. Same fallback caveat as {@link TERMINAL_WINDOW_MS}.
 */
export const TERMINAL_MAX_ATTEMPTS = 10;

/** The outcome of a single attempt. */
export type AttemptOutcome = 'success' | 'failure';

/** Inputs to {@link decideTerminalOutcome} — everything needed to decide, no state. */
export interface TerminalOutcomeInput {
  /** 1-based index of THIS attempt within the current window. */
  attemptNumber: number;
  /** Wall-clock ms from the FIRST attempt in the window to THIS attempt. */
  elapsedMs: number;
  /** Whether THIS attempt succeeded or failed. */
  outcome: AttemptOutcome;
  /**
   * Per-window attempt cap. Defaults to {@link TERMINAL_MAX_ATTEMPTS} when a host
   * supplies none (ENG-4321 B1: a host loop passes its own retry budget so the
   * window is never tighter than the loop it observes).
   */
  maxAttempts?: number;
  /** Per-window duration bound (ms). Defaults to {@link TERMINAL_WINDOW_MS}; see {@link maxAttempts}. */
  windowMs?: number;
}

/** The decision for one attempt. */
export interface TerminalOutcomeDecision {
  attemptNumber: number;
  elapsedMs: number;
  /** True when this attempt ends the window (success, or a failure with no further in-window attempt). */
  isTerminal: boolean;
  /** True only on a TERMINAL non-success — the single event the failure signal counts. */
  countsAsFailure: boolean;
  /** The outcome of this attempt, echoed so an emitter can pick the success/failure event name. */
  result: AttemptOutcome;
}

/**
 * Pure terminal-outcome decision for a single attempt (ENG-4321).
 *
 * Boundary is PINNED to the existing auto-link precedent's strict `<`
 * (auto-link-controller.ts window/attempt checks): an attempt is in-window iff
 * `elapsedMs < windowMs` (with the default TERMINAL_WINDOW_MS: 59_999ms →
 * in-window, 60_000ms → window exceeded → terminal) and the cap is terminal at
 * `attemptNumber >= maxAttempts` (with the default TERMINAL_MAX_ATTEMPTS=10:
 * attempt 10 failing with no success is terminal; attempt 9 is not). `windowMs` /
 * `maxAttempts` come from the per-window host budget (ENG-4321 B1) and fall back
 * to the module constants. Do not invent the opposite convention.
 *
 * ENG-4337 BOUNDARY: retry storms are a SEPARATE signal that feeds ENG-4337's
 * retry-storm alerting. The RAW retry volume (how many attempts happened) is
 * NOT a failure and MUST NOT be folded into {@link TerminalOutcomeDecision.countsAsFailure}
 * here — only a terminal non-success counts. Keep any retry-storm counting
 * strictly disjoint from this decision.
 */
export const decideTerminalOutcome = (input: TerminalOutcomeInput): TerminalOutcomeDecision => {
  const isSuccess = input.outcome === 'success';
  // Bounds come from the per-window budget the host passed in (ENG-4321 B1),
  // falling back to the global defaults only when a host supplies none — so the
  // counting window is never tighter than the retry loop it observes.
  const windowMs = input.windowMs ?? TERMINAL_WINDOW_MS;
  const maxAttempts = input.maxAttempts ?? TERMINAL_MAX_ATTEMPTS;
  // Strict `<` in-window test (pinned to auto-link-controller precedent): the
  // window is EXCEEDED when the attempt is NOT strictly inside it.
  const windowExceeded = !(input.elapsedMs < windowMs);
  const attemptsExhausted = input.attemptNumber >= maxAttempts;
  // A success ends the window immediately (recovered). A failure ends it only
  // when no further in-window attempt is possible.
  const isTerminal = isSuccess || windowExceeded || attemptsExhausted;
  // Count a failure ONLY on a terminal non-success (ENG-4321): a non-terminal
  // failed attempt superseded by a later in-window success is NOT a failure.
  const countsAsFailure = isTerminal && !isSuccess;
  return {
    attemptNumber: input.attemptNumber,
    elapsedMs: input.elapsedMs,
    isTerminal,
    countsAsFailure,
    result: input.outcome
  };
};

/** Per-window bookkeeping for one key inside {@link createTerminalOutcomeController}. */
interface WindowState {
  /** Stamped on the FIRST attempt so attempt 1 reads elapsedMs === 0. */
  firstAttemptAt: number | undefined;
  attemptCount: number;
  /**
   * Per-window bounds captured at {@link createTerminalOutcomeController}'s
   * beginWindow — the host loop's own retry budget, or the global fallback
   * (ENG-4321 B1). Held here so recordAttempt evaluates terminality against the
   * budget of the loop that opened the window, not the module default.
   */
  maxAttempts: number;
  windowMs: number;
  /**
   * Optional device-grain bag copied onto the terminal emit. Stored at
   * beginWindow so recordAttempt cannot forget it.
   */
  context?: StreamRecoveryContext;
}

/** Device-grain fields for a stream-recovery window. */
export type StreamRecoveryContext = {
  device_id?: string;
  agent_id?: string;
  is_embedded?: boolean;
};

/**
 * Stateful per-key controller for {@link decideTerminalOutcome} (ENG-4321).
 *
 * Mirrors the calibration completion controller's shape
 * (analytics/domains/calibration-lifecycle.ts): the terminal transition CLEARS
 * the per-key window state, so a re-rendered / re-entrant emit site that calls
 * {@link recordAttempt} again finds no active window and cannot double-fire. The
 * pure decision alone does NOT stop a double-fire from React re-renders — this
 * stateful guard is what makes the terminal event fire AT MOST ONCE per window.
 *
 * `now` is injected so tests drive the window deterministically with fake timers.
 *
 * @param emitTerminal - called EXACTLY once per window, on the terminal attempt.
 */
export const createTerminalOutcomeController = (
  emitTerminal: (decision: TerminalOutcomeDecision, context?: StreamRecoveryContext) => void,
  now: () => number = Date.now
) => {
  const windows = new Map<string, WindowState>();

  return {
    /**
     * Open a fresh window for `key` (call when a retry sequence starts).
     *
     * `budget` lets the host loop pass ITS OWN retry cap/duration so the terminal
     * window is never tighter than the loop it observes (ENG-4321 B1). Omit it and
     * the window uses the global {@link TERMINAL_MAX_ATTEMPTS} /
     * {@link TERMINAL_WINDOW_MS} fallback.
     *
     * `context` is the device-grain bag (`device_id` / `agent_id` /
     * `is_embedded`) copied onto the terminal emit.
     */
    beginWindow(
      key: string,
      budget?: { maxAttempts?: number; windowMs?: number },
      context?: StreamRecoveryContext
    ): void {
      windows.set(key, {
        firstAttemptAt: undefined,
        attemptCount: 0,
        maxAttempts: budget?.maxAttempts ?? TERMINAL_MAX_ATTEMPTS,
        windowMs: budget?.windowMs ?? TERMINAL_WINDOW_MS,
        ...(context !== undefined ? { context } : {})
      });
    },

    /**
     * Record one attempt's outcome. Returns the decision, or `undefined` when
     * there is no active window (the at-most-once guard: a stray/late call after
     * the window already resolved is a no-op). Emits the terminal event exactly
     * once, on the attempt that ends the window.
     */
    recordAttempt(key: string, outcome: AttemptOutcome): TerminalOutcomeDecision | undefined {
      const state = windows.get(key);
      if (!state) {
        return undefined;
      }
      // Stamp the window start on the first attempt so elapsedMs is measured
      // "from the first attempt in the window to this attempt" (attempt 1 === 0).
      if (state.firstAttemptAt === undefined) {
        state.firstAttemptAt = now();
      }
      state.attemptCount += 1;
      const elapsedMs = Math.max(0, now() - state.firstAttemptAt);
      const decision = decideTerminalOutcome({
        attemptNumber: state.attemptCount,
        elapsedMs,
        outcome,
        // Carry the window's captured budget so terminality honours the host
        // loop's cap/duration, not just the global default (ENG-4321 B1).
        maxAttempts: state.maxAttempts,
        windowMs: state.windowMs
      });
      if (!decision.isTerminal) {
        // Keep the window open; a later success within it supersedes this failure.
        return decision;
      }
      // Clear per-window state BEFORE emitting so a re-entrant call (React
      // re-render firing the same transition twice) finds no active window and
      // cannot open a second window / double-fire.
      const context = state.context;
      windows.delete(key);
      emitTerminal(decision, context);
      return decision;
    },

    /** Drop an active window WITHOUT emitting (the flow was torn down / reset). */
    abandonWindow(key: string): void {
      windows.delete(key);
    },

    /** True while `key` has an open (unresolved) window — for callers/tests. */
    hasOpenWindow(key: string): boolean {
      return windows.has(key);
    }
  };
};

/** Return type of {@link createTerminalOutcomeController}. */
export type TerminalOutcomeController = ReturnType<typeof createTerminalOutcomeController>;
